import type { DraftBoard, Player, Position } from '../types/fantasy-football'
import { POSITIONS } from '../types/fantasy-football'

export interface ExportRow {
  rank: number
  name: string
  position: Position
  nflTeam: string
  byeWeek: number
  adp: number
  score: number
  reason: string
}

/** Board slots joined to players, skipping any slot whose player is unknown. */
export function boardRows(board: DraftBoard, players: Player[]): ExportRow[] {
  const byId = new Map(players.map((p) => [p.id, p]))
  const rows: ExportRow[] = []
  for (const slot of board.slots) {
    const player = byId.get(slot.playerId)
    if (!player) continue
    rows.push({
      rank: slot.overallRank,
      name: player.name,
      position: player.position,
      nflTeam: player.nflTeam,
      byeWeek: player.byeWeek,
      adp: player.adp,
      score: slot.score,
      reason: slot.reason,
    })
  }
  return rows
}

// RFC 4180: quote any field containing a comma, quote or newline, and double
// internal quotes. Player names carry both (Ja'Marr Chase, "Smith, Jr."), so
// naive joining corrupts the file silently.
function csvField(value: string | number): string {
  const text = String(value)
  if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`
  return text
}

const CSV_HEADERS = ['Rank', 'Player', 'Pos', 'Team', 'Bye', 'ADP', 'Score', 'Reason']

export function buildCsv(rows: ExportRow[]): string {
  const lines = [CSV_HEADERS.join(',')]
  for (const r of rows) {
    lines.push([
      r.rank,
      csvField(r.name),
      r.position,
      r.nflTeam,
      r.byeWeek || '',
      r.adp >= 999 ? '' : r.adp.toFixed(1),
      r.score.toFixed(2),
      csvField(r.reason),
    ].join(','))
  }
  // CRLF + BOM so Excel opens it as UTF-8 rather than mangling accented names.
  return '﻿' + lines.join('\r\n') + '\r\n'
}

/** Ordered plain-text list, sized for pasting beside Yahoo's pre-rank page. */
export function buildPlainList(rows: ExportRow[]): string {
  return rows
    .map((r) => `${r.rank}. ${r.name} (${r.position} · ${r.nflTeam} · Bye ${r.byeWeek || '—'})`)
    .join('\n')
}

export interface Tier {
  tier: number
  rows: ExportRow[]
}

// A tier breaks where the value drop from the previous player is unusually
// large for that position — the point where waiting costs you real points
// rather than a rounding difference.
const TIER_BREAK = 0.08

export function tiersByPosition(rows: ExportRow[]): Record<Position, Tier[]> {
  const out = {} as Record<Position, Tier[]>
  for (const position of POSITIONS) {
    const inPosition = rows
      .filter((r) => r.position === position)
      .sort((a, b) => a.rank - b.rank)

    const tiers: Tier[] = []
    let current: ExportRow[] = []
    let previousScore: number | null = null
    const top = inPosition[0]?.score ?? 0

    for (const row of inPosition) {
      const gap = previousScore === null ? 0 : previousScore - row.score
      if (current.length && top > 0 && gap / top > TIER_BREAK) {
        tiers.push({ tier: tiers.length + 1, rows: current })
        current = []
      }
      current.push(row)
      previousScore = row.score
    }
    if (current.length) tiers.push({ tier: tiers.length + 1, rows: current })
    out[position] = tiers
  }
  return out
}

export function draftBoardFilename(board: DraftBoard): string {
  const date = (board.approvedAt || board.generatedAt || new Date().toISOString()).slice(0, 10)
  return `draft-board-${date}.csv`
}

/** Trigger a client-side file download without leaving the page. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export async function copyToClipboard(text: string): Promise<void> {
  // The Clipboard API can be present and still reject — permission denied, or
  // a non-secure context — so the fallback has to catch the throw, not just a
  // missing method. Rejecting silently here would leave the button claiming
  // success with nothing on the clipboard.
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch {
      // fall through to the textarea path
    }
  }

  const area = document.createElement('textarea')
  area.value = text
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.appendChild(area)
  area.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } finally {
    document.body.removeChild(area)
  }
  if (!ok) throw new Error('Clipboard blocked')
}
